import { useEffect, useState } from "react";
import { Link, useOutletContext, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Users, Plus, Compass, Search, Sparkles } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

export default function CommunitiesPage() {
  const nav = useNavigate();
  const { servers = [], reloadServers } = useOutletContext() || {};
  const [discover, setDiscover] = useState([]);
  const [q, setQ] = useState("");
  const [openCreate, setOpenCreate] = useState(false);
  const [newSrv, setNewSrv] = useState({ name: "", description: "" });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    api.get("/servers/discover").then((r) => setDiscover(r.data)).catch(() => {});
  }, []);

  const filtered = discover.filter((s) =>
    !q || s.name.toLowerCase().includes(q.toLowerCase()) || (s.description || "").toLowerCase().includes(q.toLowerCase())
  );

  const join = async (s) => {
    try {
      await api.post(`/servers/${s.id}/join`);
      toast.success(`Você entrou em ${s.name}`);
      reloadServers?.();
      nav(`/app/servidor/${s.id}`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Falha ao entrar"); }
  };

  const create = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const { data } = await api.post("/servers", newSrv);
      toast.success(`Comunidade "${data.name}" criada`);
      setOpenCreate(false);
      setNewSrv({ name: "", description: "" });
      reloadServers?.();
      nav(`/app/servidor/${data.id}`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Falha ao criar"); }
    finally { setCreating(false); }
  };

  return (
    <main className="flex-1 min-w-0 overflow-y-auto scrollbar-thin" data-testid="communities-page">
      <section className="relative border-b border-[hsl(var(--nx-border))] overflow-hidden">
        <div className="absolute inset-0 nx-grid-bg opacity-40 pointer-events-none" />
        <div className="relative px-6 lg:px-10 py-10 max-w-6xl">
          <div className="nx-chip nx-chip-blue mb-3"><Users className="w-3.5 h-3.5" /> Comunidades</div>
          <h1 className="font-display font-bold text-3xl sm:text-4xl">Encontre sua tribo.</h1>
          <p className="mt-2 text-muted-foreground max-w-xl">Descubra servidores abertos ou crie o seu com um clique.</p>

          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1 max-w-lg">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input placeholder="Buscar comunidades" value={q} onChange={(e) => setQ(e.target.value)}
                className="pl-10 h-11 rounded-xl bg-[hsl(var(--input))] border-[hsl(var(--nx-border))]"
                data-testid="communities-search" />
            </div>
            <Dialog open={openCreate} onOpenChange={setOpenCreate}>
              <DialogTrigger asChild>
                <Button data-testid="communities-create-btn" className="rounded-xl nx-btn-blue border-0 h-11 px-5 font-semibold">
                  <Plus className="w-4 h-4 mr-2" /> Criar comunidade
                </Button>
              </DialogTrigger>
              <DialogContent className="nx-panel">
                <DialogHeader><DialogTitle className="font-display">Criar comunidade</DialogTitle></DialogHeader>
                <form onSubmit={create} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Nome</Label>
                    <Input required minLength={2} maxLength={80} value={newSrv.name}
                      onChange={(e) => setNewSrv({ ...newSrv, name: e.target.value })}
                      data-testid="communities-create-name" />
                  </div>
                  <div className="space-y-2">
                    <Label>Descrição</Label>
                    <Textarea maxLength={280} value={newSrv.description}
                      onChange={(e) => setNewSrv({ ...newSrv, description: e.target.value })}
                      data-testid="communities-create-desc" />
                  </div>
                  <DialogFooter>
                    <Button type="submit" disabled={creating}
                      className="rounded-xl nx-btn-blue border-0 h-11 px-6 font-semibold"
                      data-testid="communities-create-submit">
                      {creating ? "criando…" : "Criar"}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </section>

      {servers.length > 0 && (
        <section className="px-6 lg:px-10 pt-10 max-w-6xl">
          <div className="flex items-end justify-between mb-4">
            <h2 className="font-display font-bold text-xl">Suas comunidades</h2>
          </div>
          <div className="grid gap-3 grid-cols-2 sm:grid-cols-3 lg:grid-cols-4">
            {servers.map((s) => (
              <Link key={s.id} to={`/app/servidor/${s.id}`} className="nx-panel p-4 hover:border-[hsl(var(--nx-blue))] transition-colors" data-testid={`mine-server-${s.id}`}>
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[hsl(var(--nx-blue))] to-[hsl(var(--nx-magenta))] grid place-items-center font-display font-bold text-white text-lg mb-3">
                  {s.name?.[0]?.toUpperCase()}
                </div>
                <div className="font-semibold text-sm truncate">{s.name}</div>
                <div className="text-[11px] text-muted-foreground truncate">{s.member_count || 1} membros</div>
              </Link>
            ))}
          </div>
        </section>
      )}

      <section className="px-6 lg:px-10 pt-12 pb-14 max-w-6xl">
        <div className="flex items-end justify-between mb-4">
          <h2 className="font-display font-bold text-xl">Descubra</h2>
          <span className="text-xs text-muted-foreground">{filtered.length} públicas</span>
        </div>
        {filtered.length === 0 ? (
          <div className="nx-panel p-10 text-center text-muted-foreground">
            <Sparkles className="w-8 h-8 mx-auto mb-3 opacity-50" />
            <p className="text-sm">Nenhuma comunidade pública ainda. Que tal criar a primeira?</p>
          </div>
        ) : (
          <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((s) => (
              <div key={s.id} className="nx-panel p-5 hover:border-[hsl(var(--nx-blue))] transition-colors" data-testid={`discover-server-${s.id}`}>
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[hsl(var(--nx-blue))] to-[hsl(var(--nx-magenta))] grid place-items-center font-display font-bold text-white text-lg shrink-0">
                    {s.name?.[0]?.toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold truncate">{s.name}</div>
                    <div className="text-[11px] text-muted-foreground">{s.member_count} membros</div>
                  </div>
                </div>
                {s.description && <p className="text-sm text-muted-foreground mt-3 line-clamp-2">{s.description}</p>}
                <div className="mt-4 flex gap-2">
                  <Button onClick={() => join(s)} className="rounded-xl nx-btn-blue border-0 h-9 px-4 text-sm font-semibold" data-testid={`join-server-${s.id}`}>
                    Entrar
                  </Button>
                  <Link to={`/app/servidor/${s.id}`} className="inline-flex items-center h-9 px-4 rounded-xl border border-[hsl(var(--nx-border))] hover:bg-white/5 text-sm">
                    Ver
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </main>
  );
}
